import { useEffect, useState } from 'react';
import { adminAPI } from '@/lib/adminApi';
import { Download, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    adminAPI.analytics(days).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  const exportCSV = async () => {
    try {
      const r = await adminAPI.exportUsers();
      const users = r.data.users;
      if (users.length === 0) return toast.error('No users to export');
      const cols = ['id', 'email', 'name', 'subscription_status', 'subscription_plan', 'onboarding_completed', 'created_at', 'last_login_at'];
      const csv = [cols.join(',')].concat(users.map(u => cols.map(c => JSON.stringify(u[c] ?? '')).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `brandos-users-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="p-10 text-purple-200/50">Loading…</div>;
  if (!data) return <div className="p-10 text-red-300">Failed to load.</div>;

  const maxSignup = Math.max(...data.daily_signups.map(d => d.count), 1);
  const maxFill = Math.max(...data.category_fills.map(c => c.filled), 1);
  const total = data.funnel.total_users || 1;
  const pctStarted = (data.funnel.started_onboarding / total) * 100;
  const pctCompleted = (data.funnel.completed_onboarding / total) * 100;

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>Analytics</h1>
          <p className="text-purple-200/50 text-sm mt-1">Trends and engagement metrics.</p>
        </div>
        <div className="flex gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            <option value={7} className="bg-purple-900">Last 7 days</option>
            <option value={30} className="bg-purple-900">Last 30 days</option>
            <option value={90} className="bg-purple-900">Last 90 days</option>
          </select>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: 'linear-gradient(145deg,#a855f7,#7c3aed)' }} data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Funnel */}
      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 mb-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Onboarding Funnel</h3>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-white">Total signups</span><span className="text-purple-200/70">{data.funnel.total_users}</span></div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-purple-500" style={{ width: '100%' }} /></div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-white">Started onboarding</span><span className="text-purple-200/70">{data.funnel.started_onboarding} ({Math.round(pctStarted)}%)</span></div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${pctStarted}%` }} /></div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-white">Completed onboarding</span><span className="text-purple-200/70">{data.funnel.completed_onboarding} ({Math.round(pctCompleted)}%)</span></div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${pctCompleted}%` }} /></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Signups Bar Chart */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Daily Signups ({days}d)</h3>
          {data.daily_signups.length === 0 ? (
            <p className="text-purple-200/40 text-sm">No signups in this period.</p>
          ) : (
            <div className="flex items-end gap-1 h-40">
              {data.daily_signups.map(d => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div className="w-full rounded-t" style={{ height: `${(d.count / maxSignup) * 100}%`, background: 'linear-gradient(180deg, #a855f7, #6d28d9)', minHeight: '2px' }} title={`${d.date}: ${d.count}`} />
                  <div className="absolute -top-7 hidden group-hover:block bg-black text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">{d.date}: {d.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Fills */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4">Category Engagement</h3>
          <div className="space-y-3">
            {data.category_fills.map(c => (
              <div key={c.slug}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-white">{c.name}</span>
                  <span className="text-purple-200/60">{c.filled} users</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.filled / maxFill) * 100}%`, background: c.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
